import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  errorMessage,
  formRegex,
  onboardingStepId,
  pagination,
  productSubsteps,
  toastMessage,
} from '@helpers/Constants';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import {
  AddonsList,
  MultiSelectionProps,
  ApiAddonResponse,
} from '@helpers/Models';
import { useLocation, useNavigate } from 'react-router-dom';
import { Assets } from '@helpers/Constants/Assets';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import Pagination from '@components/Pagination';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { isModified } from '@services/isModified';
import { responseTexts } from '@helpers/Constants';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

export const Addons: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const { updateOnboardingStep } = useLocalStorage();
  const { showConfirmationToast } = UseConfirmationToast();
  const [addon, setAddon] = useState<AddonsList[]>([]);
  const [deletedAddons, setDeletedAddons] = useState<AddonsList[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [selectedRecords, setSelectedRecords] = useState<MultiSelectionProps[]>([]);
  const [onDeletion, setOnDeletion] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [initialList, setInitialList] = useState<AddonsList[]>([]);
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tempIdCounter, setTempIdCounter] = useState(-1);

  // --- Derive hasUnsavedChanges from comparison of addon vs initialList ---
  useEffect(() => {
    setHasUnsavedChanges(isModified(initialList, addon));
  }, [addon, initialList]);

  const handleLeaveWithoutSaving = () => {
    const hasSavedEntry = addon.some((item) => item.id !== undefined);
    if (!hasSavedEntry) {
      showToastMessage(
        'At least one add‑on must be saved before you can leave this page.',
        'error'
      );
      return false;
    }
    return true;
  };

  useUnsavedChangesPrompt(hasUnsavedChanges, handleLeaveWithoutSaving, 'Leave Without Saving');

  const getInputDisplayValue = (value: number | string | undefined) => {
    return value === 0 || value === '0' ? '' : (value ?? '');
  };

  // ---- Check if there's an incomplete new row ----
  const hasIncompleteRow = (): boolean => {
    return addon.some((item) => {
      if (item.id !== undefined) return false; // saved row
      // New row: check if name is empty or price is <= 0
      const nameEmpty = !item.name || item.name.trim() === '';
      const priceInvalid = Number(item.price) <= 0;
      return nameEmpty || priceInvalid;
    });
  };

  // ---- Fetch active add‑ons (filter out deleted) ----
  const fetchAddons = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
  ): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {
        page,
        count,
        searchKey,
      });

      const allAddons = response.data.data.addOns;
      const activeAddons = allAddons.filter((a: any) => !a.deletedAt);

      const normalizedAddons = activeAddons.map((addon: ApiAddonResponse) => ({
        id: addon.id,
        name: addon.name,
        availableFlag: Boolean(addon.availableFlag),
        price: addon.price,
      }));

      setAddon(normalizedAddons);
      setInitialList(normalizedAddons);
      setCurrentPage(response.data.data.currentPage || 1);
      setTotalPages(response.data.data.totalPages || 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  // ---- Fetch deleted add‑ons (for restoration) ----
  const fetchDeletedAddons = async () => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {
        page: 1,
        count: 1000,
        searchKey: '',
      });
      const allAddons = response.data.data.addOns;
      const deleted = allAddons.filter((a: any) => a.deletedAt);
      setDeletedAddons(deleted);
    } catch (error) {
      // silently fail
    }
  };

  // Initial load
  useEffect(() => {
    fetchAddons(currentPage, searchKey, itemsPerPage);
    fetchDeletedAddons();
  }, []);

  // On search/pagination changes
  useEffect(() => {
    if (searchKey !== '') {
      setCurrentPage(1);
    }
    fetchAddons(currentPage, searchKey, itemsPerPage);
  }, [currentPage, itemsPerPage, searchKey]);

  const handleSelectAll = () => {
    const dataSelected = !allSelected;
    setAllSelected(dataSelected);
    const updatedAddons = addon.map((addon) => ({
      ...addon,
      availableFlag: dataSelected,
    }));
    setAddon(updatedAddons);
  };

  // ---- Add More: insert a new empty row ----
  const handleAddMore = () => {
    if (hasIncompleteRow()) {
      showToastMessage(
        'Please complete the current add‑on before adding another.',
        'error'
      );
      return;
    }

    const newTempId = tempIdCounter;
    setTempIdCounter((prev) => prev - 1);

    const newAddon: AddonsList = {
      name: '',
      price: 0,
      availableFlag: true,
    };
    (newAddon as any)._tempId = newTempId;

    setAddon([...addon, newAddon]);
    setTimeout(() => {
      const scrollArea = document.getElementById('addon-table-scroll-area');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100);
  };

  // ---- Validation helpers ----
  const validateField = (name: string, value: string, excludeIndex?: number): string => {
    switch (name) {
      case 'name': {
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        if (formRegex.PRICE.test(value)) {
          return errorMessage.NAME_ERROR;
        }
        if (formRegex.SPECIAL_CHARACTERS.test(value)) {
          return errorMessage.SPECIAL_CHARACTERS;
        }
        const isDuplicate = addon.some(
          (addon, index) =>
            addon.name.toLowerCase() === value.toLowerCase().trim() &&
            index !== excludeIndex
        );
        return isDuplicate ? errorMessage.DUPLICATE_ERROR : '';
      }
      case 'price': {
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        const price = parseFloat(value);
        if (isNaN(price)) {
          return errorMessage.PRICE_NUMERIC_ERROR;
        } else if (price <= 0) {
          return 'Price must be greater than 0';
        }
        return '';
      }
      default:
        return '';
    }
  };

  // ---- Validate all add-ons (including duplicates) ----
  const validateAllAddons = (): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: any } = {};
    const nameMap = new Map<string, number[]>();

    addon.forEach((item, index) => {
      const err: any = {};
      const trimmedName = item.name?.trim() || '';
      if (!trimmedName) {
        err.name = errorMessage.FIELD_REQUIRED;
        isValid = false;
      } else {
        const key = trimmedName.toLowerCase();
        if (!nameMap.has(key)) {
          nameMap.set(key, [index]);
        } else {
          nameMap.get(key)!.push(index);
        }
      }

      if (Number(item.price) <= 0) {
        err.price = 'Price must be greater than 0';
        isValid = false;
      }

      if (Object.keys(err).length) {
        newErrors[`${index}`] = err;
      }
    });

    // Mark duplicates
    nameMap.forEach((indices, _name) => {
      if (indices.length > 1) {
        indices.forEach((idx) => {
          if (!newErrors[idx]) newErrors[idx] = {};
          newErrors[idx].duplicate = 'Add-on name must be unique';
        });
        isValid = false;
      }
    });

    // Convert to errors object keyed by field
    const fieldErrors: { [key: string]: string } = {};
    Object.keys(newErrors).forEach((key) => {
      const idx = parseInt(key, 10);
      const err = newErrors[key];
      if (err.name) fieldErrors[`name_${idx}`] = err.name;
      if (err.price) fieldErrors[`price_${idx}`] = err.price;
      if (err.duplicate) fieldErrors[`name_${idx}`] = err.duplicate;
    });
    setErrors(fieldErrors);
    return isValid;
  };

  // ---- Field change handlers ----
  const handleFieldChange = (index: number, field: string, value: any) => {
    let error = '';
    if (field === 'name') {
      error = validateField('name', value, index);
    } else if (field === 'price') {
      error = validateField('price', value.toString());
    }
    setErrors((prev) => ({
      ...prev,
      [`${field}_${index}`]: error,
    }));
    const updatedAddons = [...addon];
    updatedAddons[index] = {
      ...updatedAddons[index],
      [field]: field === 'price' ? parseFloat(value) || 0 : value,
    };
    setAddon(updatedAddons);
  };

  const handleAvailableFlagChange = (index: number, value: boolean): void => {
    const updatedAddons = [...addon];
    updatedAddons[index] = {
      ...updatedAddons[index],
      availableFlag: value,
    };
    setAddon(updatedAddons);
  };

  // ---- Delete (handles both saved and unsaved) ----
  const performDelete = async (
    id: number | undefined,
    index: number,
  ): Promise<void> => {
    try {
      if (id !== undefined) {
        await Axios.delete(ApiEndpoints.DELETE_ADDONS, {
          data: { ids: [id] },
        });
        await fetchDeletedAddons();
        setAddon((prev) => prev.filter((item) => item.id !== id));
        showToastMessage('Add-on deleted successfully', 'success');
      } else {
        setAddon((prev) => prev.filter((_, i) => i !== index));
        showToastMessage('Add-on removed', 'success');
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleDelete = (id: number | undefined, index: number): void => {
    if (id !== undefined) {
      showConfirmationToast(
        'Are you sure you want to delete this item?',
        'Delete Confirmation',
        'delete',
        () => performDelete(id, index),
        'Delete',
        undefined,
        'Cancel',
      );
    } else {
      performDelete(id, index);
    }
  };

  // ---- Multiple delete selection ----
  const handleMultipleDeleteSelection = (
    id: number | undefined,
    index: number,
  ) => {
    setSelectedRecords((prev) => {
      const dataExists = prev.some((record) =>
        id !== undefined ? record.id === id : record.index === index,
      );
      return dataExists
        ? prev.filter((record) =>
          id !== undefined ? record.id !== id : record.index !== index,
        )
        : [...prev, { id, index }];
    });
  };

  const handleMultipleDelete = async (): Promise<void> => {
    try {
      const dataWithId = selectedRecords.filter(
        (record) => record.id !== undefined,
      );
      const dataWithIndex = selectedRecords.map((record) => record.index);
      if (dataWithId.length > 0) {
        const payload = dataWithId.map((record) => record.id);
        await Axios.delete(ApiEndpoints.DELETE_ADDONS, {
          data: { ids: payload },
        });
        await fetchDeletedAddons();
      }
      setAddon((prev) =>
        prev.filter((_, index) => !dataWithIndex.includes(index)),
      );
      setSelectedRecords([]);
      showToastMessage('Selected add-ons removed', 'success');
    } catch (error) {
      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };

  // ---- Save ----
  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();
    setErrors({});

    const isValid = validateAllAddons();
    if (!isValid) {
      showToastMessage(errorMessage.FORM_REQUIRED, 'error');
      return;
    }

    const isDataModified = isModified(initialList, addon);
    if (!isDataModified) {
      if (nextPage) {
        const hasSavedEntry = addon.some((item) => item.id !== undefined);
        if (!hasSavedEntry) {
          showToastMessage(
            'At least one add‑on must be saved before proceeding.',
            'error'
          );
          return;
        }
        updateOnboardingStep(onboardingStepId.ADD_ON);
        if (fromSettings) {
          navigate(FrontendRoutes.PACKAGING_TYPE, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.PACKAGING_TYPE);
        }
      }
      return;
    }

    // Count updates before modifying the list
    let updatedCount = 0;
    for (const item of addon) {
      if (item.id !== undefined) {
        const initial = initialList.find((init) => init.id === item.id);
        if (initial) {
          if (
            initial.name !== item.name ||
            initial.price !== item.price ||
            initial.availableFlag !== item.availableFlag
          ) {
            updatedCount++;
          }
        }
      }
    }

    // Prepare payload and restore IDs
    const payload: any[] = [];
    const restoreIds: number[] = [];
    let newItemsCount = 0;

    for (const item of addon) {
      const isNew = (item as any)._tempId !== undefined && !item.id;
      let finalId = item.id;

      if (isNew) {
        const matchedDeleted = deletedAddons.find(
          (d) => d.name.trim().toLowerCase() === item.name.trim().toLowerCase()
        );
        if (matchedDeleted && matchedDeleted.id !== undefined) {
          restoreIds.push(matchedDeleted.id);
          finalId = matchedDeleted.id;
        } else {
          finalId = undefined;
          newItemsCount++;
        }
      }

      const data: any = {
        name: item.name,
        price: Number(item.price),
        availableFlag: item.availableFlag ? 1 : 0,
      };
      if (finalId !== undefined) {
        data.id = finalId;
      }
      payload.push(data);
    }

    if (payload.length === 0 && restoreIds.length === 0) {
      showToastMessage(errorMessage.PRODUCT_LIST_EMPTY, 'error');
      return;
    }

    try {
      let restoredCount = 0;
      if (restoreIds.length > 0) {
        await Axios.post(ApiEndpoints.RESTORE_ADDONS, { ids: restoreIds });
        restoredCount = restoreIds.length;
        await fetchDeletedAddons();
      }

      if (payload.length > 0) {
        await Axios.post(ApiEndpoints.UPSERT_ADDONS, {
          addOns: payload,
        });
        await fetchAddons(currentPage, searchKey, itemsPerPage);
        await fetchDeletedAddons();

        // Build success message with proper pluralization
        const totalAdded = restoredCount + newItemsCount;
        let message = '';

        if (totalAdded > 0 && updatedCount > 0) {
          const addedWord = totalAdded === 1 ? 'add‑on' : 'add‑ons';
          const updatedWord = updatedCount === 1 ? 'add‑on' : 'add‑ons';
          message = `${totalAdded} ${addedWord} added and ${updatedCount} ${updatedWord} updated successfully.`;
        } else if (totalAdded > 0) {
          if (totalAdded === 1) {
            message = 'Add‑on created successfully.';
          } else {
            message = `${totalAdded} add‑ons created successfully.`;
          }
        } else if (updatedCount > 0) {
          if (updatedCount === 1) {
            message = 'Add‑on updated successfully.';
          } else {
            message = `${updatedCount} add‑ons updated successfully.`;
          }
        } else {
          // Fallback for restores only
          if (restoredCount > 0) {
            message = `${restoredCount} add‑on(s) restored successfully.`;
          } else {
            message = responseTexts.ADDON_UPSERT_SUCCESS;
          }
        }
        showToastMessage(message, 'success');
      } else {
        // Only restoration happened
        showToastMessage(`${restoredCount} add‑on(s) restored successfully.`, 'success');
      }

      updateOnboardingStep(onboardingStepId.ADD_ON);

      if (nextPage) {
        if (fromSettings) {
          navigate(FrontendRoutes.PACKAGING_TYPE, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.PACKAGING_TYPE);
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  // ---- Next handler ----
  const handleNext = () => {
    const hasSavedEntry = addon.some((item) => item.id !== undefined);
    if (!hasSavedEntry) {
      showToastMessage(
        'At least one add‑on must be saved before you can proceed.',
        'error'
      );
      return;
    }
    updateOnboardingStep(onboardingStepId.ADD_ON);
    if (fromSettings) {
      navigate(FrontendRoutes.PACKAGING_TYPE, { state: { fromSettings } });
    } else {
      navigate(FrontendRoutes.PACKAGING_TYPE);
    }
  };

  // ---- Search ----
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (value.length >= 3) {
        setSearchKey(value);
      } else {
        setSearchKey('');
      }
    }, 500);
  };

  // ---- Render ----
  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Products"
          activeStep={2}
          subNavSteps={productSubsteps}
          fromSettings={fromSettings ? true : false}
        />
      </div>

      <div className="mt-3 rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-semibold text-xl px-5 mt-3">Add-on</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={handleSearchChange}
                value={searchInput}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto mb-6 relative shadow-lg h-[calc(100vh-480px)]"
          id="addon-table-scroll-area"
        >
          <table className="w-full table-auto">
            <thead className="bg-gray-100">
              <tr className="border-b">
                <th className="px-4 py-2 text-left">
                  <div className="flex items-center gap-2 text-[16px] font-semibold text-[#1C1B1F]">
                    <span>Add-on Description</span>
                    <img
                      src={Assets.SORT_ICON}
                      alt="Sort"
                      className="h-3 w-3"
                    />
                  </div>
                </th>
                <th className="px-4 py-2 text-center font-semibold text-[#1C1B1F]">
                  Price
                </th>
                <th className="px-4 py-2 text-center font-semibold text-[#1C1B1F]">
                  Available{' '}
                  <input
                    onChange={handleSelectAll}
                    type="checkbox"
                    className="ml-2 h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                    aria-label="Select All"
                    checked={allSelected}
                  />
                </th>
                <th className="px-4 py-2 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {addon.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    No data available
                  </td>
                </tr>
              ) : (
                addon.map((addons, index) => {
                  const key = addons.id ?? (addons as any)._tempId ?? index;
                  return (
                    <tr
                      key={key}
                      className={onDeletion ? 'bg-[#F6F5F5]' : ''}
                    >
                      <td className="px-4 py-2">
                        <div className="flex items-start gap-3">
                          {onDeletion && (
                            <input
                              type="checkbox"
                              name="deletion"
                              checked={selectedRecords.some((record) =>
                                addons.id !== undefined
                                  ? record.id === addons.id
                                  : record.index === index,
                              )}
                              onChange={() =>
                                handleMultipleDeleteSelection(addons.id, index)
                              }
                              className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded mt-1"
                            />
                          )}
                          <div className="w-full">
                            <input
                              type="text"
                              name="name"
                              value={addons.name || ''}
                              onChange={(e) =>
                                handleFieldChange(index, 'name', e.target.value)
                              }
                              disabled={onDeletion}
                              className={`w-full text-[14px] font-medium text-black border-b border-gray-300 focus:border-primary focus:outline-none ${
                                errors[`name_${index}`] ? 'border-red-500' : ''
                              } ${onDeletion ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                            {errors[`name_${index}`] && (
                              <p className="text-red-500 text-[11px] mt-1">
                                {errors[`name_${index}`]}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center min-w-[160px]">
                        <div className="flex items-center justify-center">
                          <div className="flex items-center border rounded-md text-gray-600 bg-white focus-within:outline-primary">
                            <span className="px-2 text-gray-600 bg-white">$</span>
                            <input
                              name="price"
                              type="number"
                              step="0.01"
                              min="0"
                              value={getInputDisplayValue(addons.price)}
                              onChange={(e) =>
                                handleFieldChange(index, 'price', e.target.value)
                              }
                              disabled={onDeletion}
                              className={`w-16 p-2 focus:outline-none text-gray-600 ${onDeletion ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            />
                          </div>
                          <span className="ml-1 text-gray-600">/ lbs</span>
                        </div>
                        {errors[`price_${index}`] && (
                          <p className="text-red-500 text-[11px] mt-1">
                            {errors[`price_${index}`]}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleAvailableFlagChange(index, e.target.checked)
                          }
                          disabled={onDeletion}
                          checked={addons.availableFlag || false}
                          className={`h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded ${onDeletion ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-4">
                          <button
                            type="button"
                            onClick={() => handleDelete(addons.id, index)}
                            disabled={onDeletion}
                            className={
                              onDeletion ? 'opacity-50 cursor-not-allowed' : ''
                            }
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white mt-3">
        {onDeletion ? (
          <div className="flex justify-between gap-4">
            <button
              type="button"
              onClick={() => setOnDeletion(false)}
              className="w-[50%] px-6 py-1 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={() =>
                showConfirmationToast(
                  toastMessage.delteConfirmation,
                  'Remove',
                  'delete',
                  handleMultipleDelete,
                  'Remove all',
                )
              }
              className="w-[50%] px-6 py-1 border border-red-500 rounded-sm text-sm font-medium text-red-500 bg-white hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faTrashCan} />
              <span>Remove All</span>
            </button>
          </div>
        ) : (
          <div className="w-full mt-6 pb-4 pt-4 border-t border-gray-100">
            <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
              <button
                type="button"
                onClick={handleAddMore}
                className="w-full 2xl:w-[150px] h-10 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
              >
                <span>+ Add More</span>
              </button>

              <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={!hasUnsavedChanges}
                  className={`w-1/2 2xl:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                      ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                      : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                    }`}
                >
                  Save
                </button>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!addon.some((item) => item.id !== undefined)}
                  className={`w-1/2 2xl:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white flex items-center justify-center space-x-2 transition-all duration-200 ${addon.some((item) => item.id !== undefined)
                      ? 'bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                      : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                  title={
                    addon.some((item) => item.id !== undefined)
                      ? ''
                      : 'At least one add‑on must be saved'
                  }
                >
                  <span>Next</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};